import styles from './index.module.css'

import cn from 'clsx'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import { TRACK_SHARING } from '@disco/constants/FeatureTypes'
import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getUser } from '@disco/state-management/selectors/user'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { formatTimestampShort } from '@disco/utils/time-utils'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { Track } from 'types/track'
import type { User } from 'types/user'

import ContactName from 'components/common/contact-name'
import Row from 'components/common/row'
import StatsTableEmpty from 'components/common/stats-table/stats-table-empty'
import TrackInfo from 'components/common/track-info'
import EmbedTab from 'components/modals/playlist/share/embed-tab'
import { Tab, Tabs } from 'components/ui'
import Alert from 'components/ui/alert'
import CopyUrlItem from 'components/ui/copy-url-item'
import type { Column } from 'components/ui/data-table'
import DataTable from 'components/ui/data-table'
import FormNote from 'components/ui/form-note'
import Heading from 'components/ui/heading'
import { HoverProvider } from 'components/ui/hover-provider'
import InlineLink from 'components/ui/inline-link'
import ModalWindow from 'components/ui/modal-window'

import type { AggregatedStats } from './helpers'
import { getAggregates, makeSourceData, transformListenerData } from './helpers'
import SourceLink from './source-link'
import StatsSummary from './stats-summary'

type Props = {
  onClose: (...args: Array<any>) => any
  track: Track
  user: User
  isTrackSharingEnabled: boolean
  compact: boolean
  sendGenericError: typeof sendGenericError
}

const getContactNameProps = contactData => {
  if (contactData['id'] !== -1) {
    return {
      user: contactData['Name'],
      contact: {
        name: contactData['Name'],
        id: contactData['id'],
      },
    }
  }

  return {
    user: 'Other Recipients',
  }
}

const listenerColumns: Column[] = [
  {
    name: 'Name',
    render: (prop: Record<string, any>) => (
      <ContactName {...getContactNameProps(prop)} />
    ),
    width: 'content',
    toSortable: (prop: Record<string, any>) => prop['Name'],
  },
  {
    name: 'Page Views',
    type: 'numeric',
    render: (prop: Record<string, any>) => <span>{prop['Page Views']}</span>,
    width: 'small',
    toSortable: (prop: Record<string, any>) => prop['Page Views'],
  },
  {
    name: 'Downloads/Saves',
    type: 'numeric',
    render: (prop: Record<string, any>) => (
      <span>{prop['Downloads/Saves']}</span>
    ),
    width: 'small',
    toSortable: (prop: Record<string, any>) => prop['Downloads/Saves'],
  },
  {
    name: 'Streams',
    type: 'numeric',
    render: (prop: Record<string, any>) => <span>{prop['Streams']}</span>,
    width: 'small',
    toSortable: (prop: Record<string, any>) => prop['Streams'],
  },
  {
    name: 'Last Activity',
    type: 'date',
    render: (prop: Record<string, any>) => (
      <span>{formatTimestampShort(prop['Last Activity'])}</span>
    ),
    toSortable: (prop: Record<string, any>) => prop['Last Activity'],
  },
]
const sourceColumns: Column[] = [
  {
    name: 'Source',
    render: (prop: Record<string, any>) => (
      <SourceLink source={prop['Source']} />
    ),
    width: 'content',
    toSortable: (prop: Record<string, any>) =>
      (prop['Source'] as any).sourceText,
  },
  {
    name: 'Page Views',
    type: 'numeric',
    render: (prop: Record<string, any>) => <span>{prop['Page Views']}</span>,
    width: 'small',
    toSortable: (prop: Record<string, any>) => prop['Page Views'],
  },
  {
    name: 'Downloads/Saves',
    type: 'numeric',
    render: (prop: Record<string, any>) => (
      <span>{prop['Downloads/Saves']}</span>
    ),
    width: 'small',
    toSortable: (prop: Record<string, any>) => prop['Downloads/Saves'],
  },
  {
    name: 'Streams',
    type: 'numeric',
    render: (prop: Record<string, any>) => <span>{prop['Streams']}</span>,
    width: 'small',
    toSortable: (prop: Record<string, any>) => prop['Streams'],
  },
  {
    name: 'Listeners',
    type: 'numeric',
    render: (prop: Record<string, any>) => (
      <span>{(prop['uniqueListeners'] as any).length}</span>
    ),
    width: 'small',
    toSortable: (prop: Record<string, any>) =>
      (prop['uniqueListeners'] as any).length,
  },
]

class Stats extends Component<
  Props,
  {
    tabIndex: number
    listenerData: any[]
    sourceData: any[]
    aggregatedStats: AggregatedStats
    loading: boolean
    noData: boolean
  }
> {
  constructor(props: Props) {
    super(props)
    const { track } = this.props
    this.state = {
      tabIndex: 0,
      listenerData: null,
      sourceData: null,
      aggregatedStats: null,
      loading: false,
      noData: false,
    }
  }

  componentDidMount() {
    this.fetchListenerData()
  }

  async fetchListenerData() {
    const { track } = this.props
    this.setState({
      loading: true,
    })

    try {
      const rawListenerData = await api.getTrackStats(track.id)

      if (!rawListenerData || rawListenerData.length === 0) {
        this.setState({
          loading: false,
          noData: true,
        })
        return
      }

      const listenerData = transformListenerData(
        rawListenerData,
        this.props.isTrackSharingEnabled,
      )
      const aggregatedStats = getAggregates(listenerData)
      const sourceData = makeSourceData(rawListenerData, track)
      this.setState({
        listenerData,
        aggregatedStats,
        sourceData,
        loading: false,
      })
    } catch (error) {
      this.setState({
        loading: false,
      })
      this.props.sendGenericError(error)
    }
  }

  _handleTabChange = (tabIndex: number) =>
    this.setState({
      tabIndex,
    })

  render() {
    const { track } = this.props
    if (!track) return null
    return (
      <ModalWindow
        header={
          <>
            <Heading size="700">Stats for {track.name}</Heading>
          </>
        }
        isOpen
        width="large"
        onClose={this.props.onClose}
      >
        {this.state.noData ? (
          <StatsTableEmpty />
        ) : (
          <>
            <StatsSummary
              loading={this.state.loading}
              isTrackSharingEnabled={this.props.isTrackSharingEnabled}
              aggregatedStats={this.state.aggregatedStats}
            />
            <Tabs
              className={cn('Tabs--modal', styles.Tabs_stats_modal)}
              index={this.state.tabIndex}
              onChange={this._handleTabChange}
            >
              <Tab label="Listeners">
                <DataTable
                  columns={listenerColumns}
                  data={this.state.listenerData}
                  rows={
                    this.state.listenerData ? this.state.listenerData.length : 0
                  }
                />
              </Tab>
              <Tab label="Source">
                <DataTable
                  columns={sourceColumns}
                  data={this.state.sourceData}
                  rows={
                    this.state.sourceData ? this.state.sourceData.length : 0
                  }
                />
              </Tab>
            </Tabs>
            <FormNote
              className={styles.Tabs_modal_form_note}
              displayIcon={false}
            >
              To view more activity for your recipients, and manage their
              details, visit{' '}
              <InlineLink to="/contacts/list">Contacts</InlineLink>
            </FormNote>
          </>
        )}
      </ModalWindow>
    )
  }
}

export default connect(
  state => ({
    user: getUser(state),
    isTrackSharingEnabled: isFeatureEnabled(state, {
      featureName: TRACK_SHARING,
    }),
  }),
  {
    sendGenericError,
  },
)(Stats) as any
